import React, { useEffect, useState, useMemo } from "react";
import { useAuth } from "../context/AuthContext";
import OwnerQuotationInvoiceView from "../components/OwnerQuotationInvoiceView";
import API_URL from '../api/baseURL';
import { Page, Panel, Badge, Table, Tr, Td, EmptyState, Loading, BackLink, Button } from '../components/ui/Iparc';
import { fmt } from '../theme/iparc';

export default function OwnerInvoicesPage() {
  const { token } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all | unpaid | paid

  useEffect(() => {
    fetch(`${API_URL}/workshop-invoices/my`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => res.json())
      .then(data => setInvoices(Array.isArray(data) ? data : []))
      .catch(() => setInvoices([]))
      .finally(() => setLoading(false));
  }, [token]);

  const unpaidCount = useMemo(() => invoices.filter(i => !i.paid).length, [invoices]);
  const totalDue = useMemo(
    () => invoices.filter(i => !i.paid).reduce((s, i) => s + Number(i.clientTotal || i.total || 0), 0),
    [invoices]
  );

  const shown = useMemo(() => {
    if (filter === 'unpaid') return invoices.filter(i => !i.paid);
    if (filter === 'paid') return invoices.filter(i => i.paid);
    return invoices;
  }, [invoices, filter]);

  if (selected) {
    return (
      <Page width="narrow">
        <BackLink onClick={() => setSelected(null)} />
        <OwnerQuotationInvoiceView quotation={selected} type="invoice" />
      </Page>
    );
  }

  const chips = [
    { key: 'all', label: `Toutes · ${invoices.length}` },
    { key: 'unpaid', label: `Impayées · ${unpaidCount}` },
    { key: 'paid', label: `Payées · ${invoices.length - unpaidCount}` },
  ];

  return (
    <Page
      title="Mes factures"
      subtitle="Finances"
      actions={
        unpaidCount > 0 && (
          <div className="text-right">
            <p className="text-[10.5px] uppercase tracking-[.16em] text-[#6E665C]">Reste à payer</p>
            <p className="font-['Space_Grotesk',sans-serif] text-[20px] font-semibold tabular-nums text-[#F4F1EA] leading-tight">
              {fmt(totalDue)} <span className="text-[12px] text-[#8F877B]">F</span>
            </p>
          </div>
        )
      }
    >
      <Panel
        title="Factures reçues"
        bodyClass=""
        action={
          <div className="flex gap-[5px]">
            {chips.map(c => (
              <button
                key={c.key}
                onClick={() => setFilter(c.key)}
                className={`h-[26px] px-2.5 rounded-[7px] text-[11px] font-medium transition-colors ${
                  filter === c.key ? 'bg-[#241F1B] text-[#F4F1EA]' : 'text-[#6E665C] hover:text-[#C9C3B6]'
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
        }
      >
        {loading ? (
          <Loading label="Chargement des factures…" />
        ) : invoices.length === 0 ? (
          <EmptyState
            title="Aucune facture"
            hint="Les factures émises par vos ateliers apparaîtront ici."
          />
        ) : shown.length === 0 ? (
          <EmptyState title="Aucune facture dans cette vue." />
        ) : (
          <Table head={['Atelier', 'Véhicule', 'Total', 'Statut', '']} minWidth={720}>
            {shown.map(inv => (
              <Tr key={inv._id} onClick={() => setSelected(inv)}>
                <Td className="text-[#F4F1EA]">{inv.workshop?.businessName || '—'}</Td>
                <Td>
                  <span className="font-['Space_Grotesk',sans-serif] text-[12.5px] tracking-[.02em] text-[#F4F1EA]">
                    {inv.vehicle?.plateNumber || '—'}
                  </span>
                </Td>
                <Td mono>
                  {fmt(inv.clientTotal || inv.total || 0)}{' '}
                  <span className="text-[10.5px] text-[#6E665C]">F</span>
                </Td>
                <Td>
                  <Badge tone={inv.paid ? 'ok' : 'danger'}>
                    {inv.paid ? 'Payée' : 'Impayée'}
                  </Badge>
                </Td>
                <Td className="text-right">
                  <Button variant="quiet" className="h-[26px] px-2">Voir</Button>
                </Td>
              </Tr>
            ))}
          </Table>
        )}
      </Panel>
    </Page>
  );
}
